import { useCallback, useMemo, useState } from 'react'
import { notification } from 'antd'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import { useHubspotSyncRunMutation } from '../../api/client-billing-profile-api-client'
import type {
  HubspotSyncNewItemDto,
  HubspotSyncResultDto,
  HubspotSyncUpdatedItemDto,
} from '../../api/dto/client-billing-profile.dto'
import {
  buildReviewCompanyRows,
  buildReviewRecipientRows,
  buildSyncHeading,
  buildSyncTasks,
  currentRecipients,
  hasPendingAdditions,
  isSubmittableNewItem,
  type ReviewCompanyRow,
  type ReviewRecipientRow,
  type SyncPhase,
} from './hubspotSync.helpers'

const EMPTY_RESULT = {
  newItems: [] as HubspotSyncNewItemDto[],
  changes: [] as HubspotSyncUpdatedItemDto[],
  reviewRecipients: [] as ReviewRecipientRow[],
  reviewCompanies: [] as ReviewCompanyRow[],
}

/**
 * The sync run and the work it leaves behind. Recipient lists live here rather
 * than in the modals that edit them, so the Changes and Needs review modals
 * always send the same profile the same full list and never undo each other.
 */
export const useHubspotSyncRun = () => {
  const [phase, setPhase] = useState<SyncPhase>('idle')
  const [result, setResult] = useState(EMPTY_RESULT)
  const [recipientsByProfileId, setRecipientsByProfileId] = useState<
    Record<number, string[]>
  >({})
  // Bumped once per finished run so the modals can drop the choices an operator
  // made about the previous one; a company reappearing must arrive undecided.
  const [runId, setRunId] = useState(0)

  const runMutation = useHubspotSyncRunMutation()

  // Honor the same test-data toggle the table and stats use, so sync never
  // omits clients the admin can still see on the page.
  const { showTestData } = getAdminSettings().finance

  const runSync = useCallback(() => {
    // A run already in flight owns the result; starting a second would race it
    // and leave whichever finished last in charge.
    if (phase === 'running') return
    const previousPhase = phase
    setPhase('running')
    runMutation.mutate(
      // A second click in the same session is a deliberate re-sync, so it goes
      // past the server's cached HubSpot pull. The first one may reuse it.
      { excludeTestClients: !showTestData, force: phase !== 'idle' },
      {
        onSuccess: (data: HubspotSyncResultDto) => {
          setResult({
            newItems: data.newItems.filter(isSubmittableNewItem),
            changes: data.updatedItems.filter(hasPendingAdditions),
            reviewRecipients: buildReviewRecipientRows(data.updatedItems),
            reviewCompanies: buildReviewCompanyRows(
              data.newItems,
              data.errorItems ?? []
            ),
          })
          setRecipientsByProfileId(
            Object.fromEntries(
              data.updatedItems.map((item) => [
                item.clientBillingProfileId,
                currentRecipients(item),
              ])
            )
          )
          setRunId((curr) => curr + 1)
          setPhase('ready')
        },
        // A failed re-run leaves the previous result in place: its outstanding
        // work is still valid, and clearing the banner would be the only way
        // back to it.
        onError: () => {
          setPhase(previousPhase)
          notification.error({
            message:
              previousPhase === 'ready'
                ? 'Failed to run HubSpot sync. The previous result is still shown.'
                : 'Failed to run HubSpot sync',
          })
        },
      }
    )
  }, [runMutation.mutate, showTestData, phase])

  const resolveNewCompanies = useCallback((hubspotCompanyIds: string[]) => {
    const done = new Set(hubspotCompanyIds)
    setResult((curr) => ({
      ...curr,
      newItems: curr.newItems.filter(
        (item) => !done.has(item.hubspotCompanyId)
      ),
    }))
  }, [])

  const resolveChange = useCallback((clientBillingProfileId: number) => {
    setResult((curr) => ({
      ...curr,
      changes: curr.changes.filter(
        (item) => item.clientBillingProfileId !== clientBillingProfileId
      ),
    }))
  }, [])

  const resolveReviewRows = useCallback((keys: string[]) => {
    const done = new Set(keys)
    setResult((curr) => ({
      ...curr,
      reviewRecipients: curr.reviewRecipients.filter(
        (row) => !done.has(row.key)
      ),
    }))
  }, [])

  const setRecipients = useCallback(
    (clientBillingProfileId: number, emails: string[]) =>
      setRecipientsByProfileId((curr) => ({
        ...curr,
        [clientBillingProfileId]: emails,
      })),
    []
  )

  // Unsyncable companies are excluded: they need fixing in HubSpot, so nothing
  // here can resolve them and counting them would pin the heading above zero.
  const pendingCount =
    result.newItems.length +
    result.changes.length +
    result.reviewRecipients.length

  const tasks = useMemo(
    () =>
      buildSyncTasks({
        newCount: result.newItems.length,
        changesCount: result.changes.length,
        reviewCount: result.reviewRecipients.length,
        unsyncedCount: result.reviewCompanies.length,
      }),
    [result]
  )

  return {
    phase,
    runId,
    newItems: result.newItems,
    changes: result.changes,
    reviewRecipients: result.reviewRecipients,
    reviewCompanies: result.reviewCompanies,
    recipientsByProfileId,
    tasks,
    heading: buildSyncHeading(pendingCount),
    runSync,
    resolveNewCompanies,
    resolveChange,
    resolveReviewRows,
    setRecipients,
  }
}
